import { z } from 'zod';
import {
  PCMP_FAILURE_STAGES,
  PCMP_PAYMENT_TYPES,
  type PcmpAccountEditPayload,
} from './pcmpProposal.types';
import {
  pcmpPageMetaSchema,
  pcmpQueueItemSchema,
  pcmpQueuePersonSchema,
  pcmpQueueSearchShape,
  type PcmpQueueFilters,
} from './pcmpQueue.types';

// GET /pcmp/correction/* and the proposal's correction actions (API LB-764).

/** The failed payment a correction answers. */
export const pcmpCorrectionFailureSchema = z.object({
  disbursementId: z.coerce.number(),
  /** The batch it failed in. */
  batchNo: z.number().nullable(),
  failureStage: z.enum(PCMP_FAILURE_STAGES),
  failureStageLabel: z.string(),
  failureReason: z.string().nullable(),
  failedAt: z.string().nullable(),
  failedBy: pcmpQueuePersonSchema.nullable(),
});

export type PcmpCorrectionFailure = z.infer<typeof pcmpCorrectionFailureSchema>;

export const pcmpCorrectionQueueItemSchema = pcmpQueueItemSchema.extend({
  paymentType: z.enum(PCMP_PAYMENT_TYPES),
  paymentTypeLabel: z.string(),
  /** Null only for a migrated item with no failed attempt. */
  failure: pcmpCorrectionFailureSchema.nullable(),
  /** The viewer may correct the account and approve the correction. */
  canCorrect: z.boolean(),
  correctionBlockedReason: z.string().nullable(),
});

export type PcmpCorrectionQueueItem = z.infer<typeof pcmpCorrectionQueueItemSchema>;

export const paginatedPcmpCorrectionQueueSchema = z.object({
  data: z.array(pcmpCorrectionQueueItemSchema),
  meta: pcmpPageMetaSchema,
});

export type PaginatedPcmpCorrectionQueue = z.infer<typeof paginatedPcmpCorrectionQueueSchema>;

export const pcmpCorrectionQueueCountsSchema = z.object({
  correction: z.number(),
  correctionAmount: z.number(),
});

export type PcmpCorrectionQueueCounts = z.infer<typeof pcmpCorrectionQueueCountsSchema>;

export interface PcmpCorrectionQueueParams extends PcmpQueueFilters {
  page: number;
  limit: number;
}

export const pcmpCorrectionQueueSearchSchema = z.object(pcmpQueueSearchShape);

export type PcmpCorrectionQueueSearch = z.infer<typeof pcmpCorrectionQueueSearchSchema>;

/** The corrected bank or wallet details; the comment is required. */
export interface PcmpCorrectionPaymentPayload {
  account: PcmpAccountEditPayload;
  comment: string;
}

export interface PcmpCorrectionApprovePayload {
  comment: string;
}

export const pcmpCorrectionApprovalResultSchema = z.object({
  proposalId: z.coerce.number(),
  eventId: z.coerce.number(),
  accountsStatus: z.string(),
  overallStatus: z.string(),
});

export type PcmpCorrectionApprovalResult = z.infer<typeof pcmpCorrectionApprovalResultSchema>;
